/**
 * Constants for the Moderated Assets view (/assets/moderated).
 *
 * This view is intentionally independent of the main Search page: it owns its
 * own request payload, query keys and filter state so changes here can never
 * leak into Search behaviour.
 */

export const MODERATED_ASSETS_ROUTE = '/assets/moderated'
export const CULTURAL_SENSITIVITY_ASSESSMENT = 'cultural_sensitivity_assessment'

// Filter payload keys for the v3 search API.
// `flagged` narrows to assets the moderation model flagged at all; the
// `flagged.categories` key narrows those down to specific categories.
export const FILTER_MODERATION_FLAGGED_KEY =
  'metadata.ai.moderationResult.flagged'
export const FILTER_MODERATION_CATEGORIES_KEY =
  'metadata.ai.moderationResult.categories'
export const FILTER_CULTURAL_SENSITIVITY_ASSESSMENT =
  'metadata.ai.culturalSensitivityAssessment.overall'
export const FILTER_CULTURAL_SENSITIVITY_DESCRIPTION_EXISTS =
  'metadata.ai.culturalSensitivityAssessment.description.exists'
export const FILTER_CULTURAL_SENSITIVITY_SCORE =
  'metadata.ai.culturalSensitivityAssessment.score'

export const FILTER_CULTURAL_SENSITIVITY_ASSESSMENT_VALUE = 'routeToHuman'
export const FILTER_CULTURAL_SENSITIVITY_DESCRIPTION_EXISTS_VALUE = 'true'

export const CULTURAL_FILTER_OPTIONS = {
  ALL: 'all',
  NEED_HUMAN_REVIEW: 'need-human-review',
}

export const MODERATION_SEVERITY_MIN = 0
export const MODERATION_SEVERITY_MAX = 100
export const MODERATION_DEFAULT_SEVERITY_MIN = 25
export const MODERATION_SEVERITY_MEDIUM_START = 30
export const MODERATION_SEVERITY_HIGH_START = 60
export const MODERATION_SEVERITY_MARKS = [0, 20, 40, 60, 80, 100]

export const MODERATION_VIEW_MODES = {
  AI_MODERATED: 'ai-moderated',
  CULTURAL_VALIDATION: 'cultural-validation',
  CULTURAL_VALIDATION_V2: 'cultural-validation-v2',
  AGENT_ASSESSMENT: 'agent-assessment',
}

// Every tab the page can show, in display order.
export const MODERATION_TAB_OPTIONS = [
  { value: MODERATION_VIEW_MODES.AI_MODERATED, label: 'AI Moderation' },
  {
    value: MODERATION_VIEW_MODES.CULTURAL_VALIDATION,
    label: 'AssetHub Original',
  },
  {
    value: MODERATION_VIEW_MODES.CULTURAL_VALIDATION_V2,
    label: 'Item Modified',
  },
  {
    value: MODERATION_VIEW_MODES.AGENT_ASSESSMENT,
    label: 'Agent Assessment',
  },
]

/**
 * Tabs to show, driven by TAP config (`MODERATION_TABS` in config.json, read
 * via useEnv): an array of view-mode values. Falls back to every tab when the
 * key is missing, empty, or lists nothing valid. Display order stays fixed.
 */
export const getEnabledModerationTabs = (configuredTabs) => {
  const configured = Array.isArray(configuredTabs) ? configuredTabs : []
  const enabled = MODERATION_TAB_OPTIONS.filter(({ value }) =>
    configured.includes(value),
  )

  return enabled.length > 0 ? enabled : MODERATION_TAB_OPTIONS
}

export const CULTURAL_SENSITIVITY_ASSESSMENT_V2 =
  'cultural_sensitivity_assessment_v2'

export const AGENT_ASSESSMENT = 'agentAssessment'

/** True for every cultural-sensitivity tab (V1 and V2). */
export const isCulturalViewMode = (viewMode) =>
  viewMode === MODERATION_VIEW_MODES.CULTURAL_VALIDATION ||
  viewMode === MODERATION_VIEW_MODES.CULTURAL_VALIDATION_V2 || 
  viewMode === MODERATION_VIEW_MODES.AGENT_ASSESSMENT

export const isAgentAssessmentViewMode = (viewMode) =>
  viewMode === MODERATION_VIEW_MODES.AGENT_ASSESSMENT

export const getAgentAssessmentKeys = () => ({
  camel: AGENT_ASSESSMENT,
  descriptionExistsFilterKey: 'metadata.ai.agentAssessment.description.exists',
  reviewFilterKey: 'metadata.ai.agentAssessment.review',
  scoreFilterKey: 'metadata.ai.agentAssessment.score',
})

/**
 * The V2 tab behaves exactly like the V1 tab but reads/filters/sorts on
 * `culturalSensitivityAssessmentV2` (snake_case: `..._v2`) instead of
 * `culturalSensitivityAssessment`. Everything that names the assessment goes
 * through here so the two tabs cannot drift apart.
 */
export const getCulturalAssessmentKeys = (viewMode) => {
  const isV2 = viewMode === MODERATION_VIEW_MODES.CULTURAL_VALIDATION_V2
  const camel = isV2
    ? 'culturalSensitivityAssessmentV2'
    : 'culturalSensitivityAssessment'

  return {
    camel,
    snake: isV2
      ? CULTURAL_SENSITIVITY_ASSESSMENT_V2
      : CULTURAL_SENSITIVITY_ASSESSMENT,
    overallFilterKey: `metadata.ai.${camel}.overall`,
    descriptionExistsFilterKey: `metadata.ai.${camel}.description.exists`,
    scoreFilterKey: `metadata.ai.${camel}.score`,
  }
}

/**
 * Fields requested back from the v3 search API for this view.
 *
 * Sent as `includeFields` on the moderated-assets payload so the response only
 * carries what the card and detail drawer actually read. This list is scoped to
 * the moderated view — the Search page payload is untouched.
 */
export const MODERATION_SEARCH_INCLUDE_FIELDS = [
  'assetId',
  'isGenAiReady',
  'uploadedDate',
  'status',
  'isDerivative',
  'masterChannel',
  'metadata.fileMetadata.fileName',
  'metadata.fileMetadata.fileType',
  'metadata.fileMetadata.fileSizeMb',
  'metadata.fileMetadata.contentType',
  'metadata.fileMetadata.originalDocumentId',
  'metadata.usageInfo',
  'metadata.creativeUsageInstructions.doNotUse',
  'isReusable',
  'isArchived',
  'metadata.assetClassification.classificationId',
  'metadata.assetClassification.secondaryClassification',
  'metadata.projectInfo.channel',
  'metadata.itemInfo.brand',
  'metadata.itemInfo.merchandiseClassification.departmentName',
  'metadata.itemInfo.merchandiseClassification.className',
  'urls',
  FILTER_MODERATION_CATEGORIES_KEY,
  FILTER_MODERATION_FLAGGED_KEY,
  'metadata.ai.culturalSensitivityAssessment.description',
  'metadata.ai.culturalSensitivityAssessment.score',
  'metadata.ai.culturalSensitivityAssessmentV2.description',
  'metadata.ai.culturalSensitivityAssessmentV2.score',
  'metadata.ai.culturalSensitivityAssessmentV2.finalReviewDecision',
  'metadata.ai.culturalSensitivityAssessmentV2.finalReviewReasoning',
  'metadata.ai.agentAssessment.description',
  'metadata.ai.agentAssessment.createdDate',
  'metadata.ai.agentAssessment.updatedDate',
  'metadata.ai.agentAssessment.score',
  'metadata.ai.agentAssessment.review',
  'metadata.ai.agentAssessment.reason',
]

// Chip value used for the default, un-narrowed view.
export const MODERATION_CATEGORY_ALL = 'all'

/**
 * Every moderation category, in chip display order.
 *
 * Keys are the moderation model's own category names — they are sent verbatim
 * as `flagged.categories` filter values AND used to look up the label for the
 * flags returned on an asset (`metadata.ai.moderationResult.categories`), so
 * this map is the single source of truth for both.
 *
 * Asset keys are normalized to lower case with `/` separators before lookup,
 * so both the raw API casing and the camelCased form produced by the search
 * mapper (`self-harm/intent` -> `selfHarm/intent`) resolve correctly.
 */
export const MODERATION_CATEGORY_LABELS = {
  sexual: 'Sexual',
  'sexual/minors': 'Sexual / Minors',
  hate: 'Hate',
  'hate/threatening': 'Hate / Threatening',
  harassment: 'Harassment',
  'harassment/threatening': 'Harassment / Threatening',
  'self-harm': 'Self-harm',
  'self-harm/intent': 'Self-harm / Intent',
  'self-harm/instructions': 'Self-harm / Instructions',
  violence: 'Violence',
  'violence/graphic': 'Violence / Graphic',
  illicit: 'Illicit',
  'illicit/violent': 'Illicit / Violent',
}

/**
 * Category chips rendered on the page, derived from
 * MODERATION_CATEGORY_LABELS so chip labels and card labels never drift.
 */
export const MODERATION_CATEGORY_OPTIONS = Object.entries(
  MODERATION_CATEGORY_LABELS,
).map(([value, label]) => ({ value, label }))

/**
 * Channel pills shown under the view tabs. Stakeholder groupings do not map
 * 1:1 to AssetHub channels, so each pill has a display label and the internal
 * channel value(s) sent as `metadata.projectInfo.channel` in the search payload.
 *
 * Pills are organized into three groups, separated by visual dividers in the UI.
 */

export const MODERATION_CHANNEL_GROUP_1 = [
  {
    value: 'digital',
    label: 'Digital .com/app and email/push',
    channels: ['Slingshot'],
  },
  { value: 'digital-dwa', label: 'Digital - DWA', channels: ['Weekly Ad'] },
  {
    value: 'store-experience',
    label: 'Store Experience',
    channels: ['Store Experience Marketing'],
  },
  { value: 'bdl', label: 'BDL', channels: ['Packaging', 'Brand Assets'] },
  {
    value: 'owned-brand-tcin',
    label: 'Owned Brand TCIN',
    channels: ['Target.com PDP Owned Brand'],
  },
  {
    value: 'campaign-paid-media',
    label: 'Campaign: Paid Media',
    channels: ['Campaign: Paid Media'],
  },
  {
    value: 'lto-design-partnerships',
    label: 'LTO/Design Partnerships',
    channels: ['Cross Channel'],
  },
]

export const MODERATION_CHANNEL_GROUP_2 = [
  {
    value: 'ugc',
    label: 'UGC',
    channels: ['Finds', 'Social'],
  },
  {
    value: 'club-target',
    label: 'Club Target',
    channels: ['Club Target'],
  },
]

export const MODERATION_CHANNEL_GROUP_3 = [
  { value: 'roundel', label: 'Roundel', channels: ['Roundel'] },
  {
    value: 'vendor-tcin',
    label: 'Vendor TCIN',
    channels: ["Target.com PDP National Brand", "Target.com", "Target.com PDP Target Plus"],
  },
]

export const MODERATION_CHANNEL_OPTIONS = [
  ...MODERATION_CHANNEL_GROUP_1,
  { value: 'divider-1', isDivider: true },
  ...MODERATION_CHANNEL_GROUP_2,
  { value: 'divider-2', isDivider: true },
  ...MODERATION_CHANNEL_GROUP_3,
]

/**
 * Review actions offered in the Review Assets dialog. `value` is sent as
 * `update_data.moderation_review_status` (the key, not the label) to the
 * metadata update API.
 */
export const MODERATION_REVIEW_STATUS = {
  PENDING: 'PENDING',
  APPROVE: 'APPROVE',
  REJECT_NEEDS_FURTHER_REVIEW: 'REJECT_NEEDS_FURTHER_REVIEW',
  REJECT_DO_NOT_USE: 'REJECT_DO_NOT_USE',
}

// Search filter key for the review status (values are the status keys).
export const FILTER_MODERATION_REVIEW_STATUS_KEY = 'moderationReviewStatus'
export const FILTER_MODERATION_AI_REVIEW_STATUS_KEY =
  'metadata.ai.culturalSensitivityAssessmentV2.finalReviewDecision'

export const MODERATION_REVIEW_LABELS = {
  [MODERATION_REVIEW_STATUS.PENDING]: 'Pending',
  [MODERATION_REVIEW_STATUS.APPROVE]: 'Approve',
  [MODERATION_REVIEW_STATUS.REJECT_NEEDS_FURTHER_REVIEW]:
    'Reject - Needs Further Review',
  [MODERATION_REVIEW_STATUS.REJECT_DO_NOT_USE]: 'Reject - Do Not Use',
}

export const AI_REVIEW_DECISION_STATUS = {
  ACCEPT: 'Accept',
  REVIEW: 'Review',
  REJECT: 'Reject',
  INCOMPLETE: 'Incomplete',
}

export const AI_REVIEW_DECISION_LABELS = {
  [AI_REVIEW_DECISION_STATUS.ACCEPT]: 'Accept',
  [AI_REVIEW_DECISION_STATUS.REVIEW]: 'Review',
  [AI_REVIEW_DECISION_STATUS.REJECT]: 'Reject',
  [AI_REVIEW_DECISION_STATUS.INCOMPLETE]: 'Incomplete',
}

export const AI_REVIEW_DECISION_OPTIONS = Object.values(
  AI_REVIEW_DECISION_STATUS,
).map((value) => ({ value, label: AI_REVIEW_DECISION_LABELS[value] }))

// Chip colors per review status, shared by the asset card and the preview panel.
export const MODERATION_REVIEW_CHIP_COLORS = {
  [MODERATION_REVIEW_STATUS.PENDING]: {
    background: '#f2f3f5',
    color: '#1b1d23',
  },
  [MODERATION_REVIEW_STATUS.APPROVE]: {
    background: '#2E7D32',
    color: '#ffffff',
  },
  [MODERATION_REVIEW_STATUS.REJECT_NEEDS_FURTHER_REVIEW]: {
    background: '#F9C74F',
    color: '#1b1d23',
  },
  [MODERATION_REVIEW_STATUS.REJECT_DO_NOT_USE]: {
    background: '#CC0000',
    color: '#ffffff',
  },
}

export const MODERATION_REVIEW_OPTIONS = Object.values(
  MODERATION_REVIEW_STATUS,
).map((value) => ({ value, label: MODERATION_REVIEW_LABELS[value] }))

// Sort menu for the moderated view. Labels feed the shared Search sort dropdown
// (CustomDropdown); the payloads go straight into `sort_fields` so the backend
// sorts the whole filtered result set, not just the loaded page.
export const MODERATION_SORT_OPTIONS = {
  uploadDateDESC: 'Date: New to Old',
  uploadDateASC: 'Date: Old to New',
  scoreASC: 'Score: Lowest to Highest',
  scoreDESC: 'Score: Highest to Lowest',
}

// Score sorts depend on the tab: each cultural tab sorts on its own score.
export const getModerationSortFields = (
  sortBy = MODERATION_DEFAULT_SORT,
  viewMode = MODERATION_VIEW_MODES.AI_MODERATED,
) => {
  if (viewMode === MODERATION_VIEW_MODES.AGENT_ASSESSMENT) {
    switch (sortBy) {
      case 'uploadDateASC':
        return { uploadedDate: 'asc' }
      case 'uploadDateDESC':
      default:
        return { uploadedDate: 'desc' }
    }
  }

  const scoreKey = getCulturalAssessmentKeys(viewMode).scoreFilterKey

  switch (sortBy) {
    case 'uploadDateASC':
      return { uploadedDate: 'asc' }
    case 'scoreASC':
      return { [scoreKey]: 'asc' }
    case 'scoreDESC':
      return { [scoreKey]: 'desc' }
    case 'uploadDateDESC':
    default:
      return { uploadedDate: 'desc' }
  }
}
export const MODERATION_DEFAULT_SORT = 'uploadDateDESC'

export const MODERATION_PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500]
export const MODERATION_DEFAULT_ROWS_PER_PAGE = 100

export const MODERATION_SEARCH_STALE_TIME_MS = 60 * 1000
export const MODERATION_SEARCH_GC_TIME_MS = 10 * 60 * 1000

export const MODERATION_CARD_COLUMN_WIDTH = 320
export const MODERATION_CARD_ROW_HEIGHT = 426
export const MODERATION_CARD_ROW_OVERSCAN = 2
export const MODERATION_PRIORITY_ABOVE_FOLD_ROWS = 2
